/**
 * IFRAP-AI.fr - Animated Infographics Component
 * Interactive visualizations from "Face au mur" by Agnès Verdier-Molinié
 */

import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp, TrendingDown, Euro, BarChart3, PieChart, ArrowRight } from 'lucide-react';

// Format large numbers in French style
const formatNumber = (num, suffix = ' Mds€') => {
  if (!num && num !== 0) return '-';
  return num.toLocaleString('fr-FR') + suffix;
};

// Simple animated number component
const AnimatedNumber = ({ value, duration = 1500 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    let start = 0;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out quad
      const eased = 1 - (1 - progress) * (1 - progress);
      setDisplayValue(Math.floor(value * eased));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    if (value > 0) {
      requestAnimationFrame(animate);
    }
    
    return () => {};
  }, [value, duration]);
  
  return <span>{displayValue}</span>;
};

// Bar chart for France vs Zone Euro comparison
const ComparisonBarChart = ({ data, title, subtitle, source }) => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.2 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Guard against missing data
  if (!data || !data.categories || !Array.isArray(data.categories)) {
    return null;
  }

  const maxValue = Math.max(...data.categories.map(c => Math.max(c.france_pib || 0, c.zone_euro_pib || 0)));
  const totalEcart = isVisible ? (data.total_ecart_mds || 0) : 0;

  return (
    <div 
      ref={containerRef}
      className="bg-gradient-to-br from-[#0a0a1a] to-[#1a1a2e] rounded-xl border border-gray-800 p-6 overflow-hidden"
    >
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-white mb-1">{title}</h3>
        <p className="text-gray-400 text-sm">{subtitle}</p>
      </div>

      {/* Total highlight */}
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <span className="text-gray-300">Surcoût total France</span>
          <div className="flex items-center gap-2">
            <span className="text-3xl font-bold text-red-400">
              <AnimatedNumber value={totalEcart} />
            </span>
            <span className="text-red-400 text-lg">Mds€/an</span>
          </div>
        </div>
      </div>

      {/* Bars */}
      <div className="space-y-4">
        {data.categories.map((cat, idx) => (
          <div key={cat.name} className="relative">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-400 truncate max-w-[50%]">{cat.name}</span>
              <span className="text-red-400 font-medium">+{cat.ecart_mds} Mds€</span>
            </div>
            <div className="flex gap-1 h-6">
              {/* France bar */}
              <div 
                className="bg-gradient-to-r from-blue-600 to-blue-500 rounded-sm relative overflow-hidden transition-all duration-1000 ease-out"
                style={{ 
                  width: isVisible ? `${(cat.france_pib / maxValue) * 100}%` : '0%',
                  transitionDelay: `${idx * 100}ms`
                }}
              >
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-white font-medium">
                  {cat.france_pib}%
                </span>
              </div>
            </div>
            <div className="flex gap-1 h-4 mt-1">
              {/* Zone Euro bar */}
              <div 
                className="bg-gradient-to-r from-gray-600 to-gray-500 rounded-sm relative transition-all duration-1000 ease-out"
                style={{ 
                  width: isVisible ? `${(cat.zone_euro_pib / maxValue) * 100}%` : '0%',
                  transitionDelay: `${idx * 100 + 50}ms`
                }}
              >
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-gray-300">
                  {cat.zone_euro_pib}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex gap-6 mt-6 pt-4 border-t border-gray-800">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-blue-500 rounded-sm"></div>
          <span className="text-xs text-gray-400">France (% PIB)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-gray-500 rounded-sm"></div>
          <span className="text-xs text-gray-400">Zone Euro (% PIB)</span>
        </div>
      </div>

      {/* Source */}
      <p className="text-[10px] text-gray-600 mt-4">{source}</p>
    </div>
  );
};

// Waterfall chart for spending evolution
const WaterfallChart = ({ data, title, subtitle, source }) => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.2 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Guard against missing data
  if (!data || !data.items || !Array.isArray(data.items)) {
    return null;
  }

  const maxAmount = Math.max(...data.items.map(item => item.amount || 0));
  const total = isVisible ? (data.total || 0) : 0;

  return (
    <div 
      ref={containerRef}
      className="bg-gradient-to-br from-[#0a0a1a] to-[#1a1a2e] rounded-xl border border-gray-800 p-6 overflow-hidden"
    >
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-white mb-1">{title}</h3>
        <p className="text-gray-400 text-sm">{subtitle}</p>
      </div>

      {/* Total highlight */}
      <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <span className="text-gray-300 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-orange-400" />
            Hausse totale 2017-2023
          </span>
          <div className="flex items-center gap-2">
            <span className="text-3xl font-bold text-orange-400">
              +<AnimatedNumber value={total} />
            </span>
            <span className="text-orange-400 text-lg">Mds€</span>
          </div>
        </div>
      </div>

      {/* Waterfall bars */}
      <div className="space-y-3">
        {data.items.map((item, idx) => (
          <div key={item.name} className="flex items-center gap-3">
            <div className="w-32 text-right">
              <span className="text-sm text-gray-400 truncate block">{item.name}</span>
            </div>
            <div className="flex-1 h-8 bg-gray-900 rounded relative overflow-hidden">
              <div 
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-orange-600 to-orange-400 rounded flex items-center transition-all duration-1000 ease-out"
                style={{ 
                  width: isVisible ? `${(item.amount / maxAmount) * 100}%` : '0%',
                  transitionDelay: `${idx * 150}ms`
                }}
              >
                <span className="absolute right-2 text-xs font-bold text-white">
                  +{item.amount} Mds€
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Source */}
      <p className="text-[10px] text-gray-600 mt-6">{source}</p>
    </div>
  );
};

// Stacked bar chart
const StackedBarChart = ({ data, title, subtitle, source }) => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.2 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Guard against missing data
  if (!data || !data.categories || !Array.isArray(data.categories)) {
    return null;
  }

  const colors = [
    'bg-blue-500', 'bg-green-500', 'bg-orange-500', 
    'bg-purple-500', 'bg-pink-500', 'bg-cyan-500'
  ];

  return (
    <div 
      ref={containerRef}
      className="bg-gradient-to-br from-[#0a0a1a] to-[#1a1a2e] rounded-xl border border-gray-800 p-6"
    >
      <div className="mb-6">
        <h3 className="text-xl font-bold text-white mb-1">{title}</h3>
        <p className="text-gray-400 text-sm">{subtitle}</p>
      </div>

      <div className="space-y-4">
        {data.categories?.map((cat, idx) => (
          <div key={cat.name}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-300">{cat.name}</span>
              <span className="text-white font-medium">{cat.france_mds} Mds€</span>
            </div>
            <div className="h-4 bg-gray-900 rounded overflow-hidden">
              <div 
                className={`h-full ${colors[idx % colors.length]} transition-all duration-1000 ease-out`}
                style={{ 
                  width: isVisible ? '100%' : '0%',
                  transitionDelay: `${idx * 100}ms`
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <p className="text-[10px] text-gray-600 mt-6">{source}</p>
    </div>
  );
};

// Line chart for trends
const LineChart = ({ data, title, subtitle, source }) => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.2 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  if (!data.years || !data.values) return null;

  const maxVal = Math.max(...data.values);
  const minVal = Math.min(...data.values);
  const range = maxVal - minVal || 1;

  // Generate SVG path
  const width = 400;
  const height = 150;
  const padding = 30;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  const points = data.values.map((val, idx) => {
    const x = padding + (idx / (data.values.length - 1)) * chartWidth;
    const y = height - padding - ((val - minVal) / range) * chartHeight;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div 
      ref={containerRef}
      className="bg-gradient-to-br from-[#0a0a1a] to-[#1a1a2e] rounded-xl border border-gray-800 p-6"
    >
      <div className="mb-4">
        <h3 className="text-xl font-bold text-white mb-1">{title}</h3>
        <p className="text-gray-400 text-sm">{subtitle}</p>
      </div>

      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
          <line 
            key={i}
            x1={padding} 
            y1={padding + chartHeight * pct}
            x2={width - padding}
            y2={padding + chartHeight * pct}
            stroke="#333"
            strokeDasharray="4,4"
          />
        ))}
        
        {/* Line */}
        <polyline
          points={points}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="transition-all duration-2000"
          style={{
            strokeDasharray: isVisible ? '0' : '1000',
            strokeDashoffset: isVisible ? '0' : '1000'
          }}
        />
        
        {/* Points */}
        {data.values.map((val, idx) => {
          const x = padding + (idx / (data.values.length - 1)) * chartWidth;
          const y = height - padding - ((val - minVal) / range) * chartHeight;
          return (
            <g key={idx}>
              <circle
                cx={x}
                cy={y}
                r={isVisible ? 5 : 0}
                fill="#3b82f6"
                className="transition-all duration-500"
                style={{ transitionDelay: `${idx * 100 + 500}ms` }}
              />
              <text 
                x={x} 
                y={height - 8}
                textAnchor="middle"
                fill="#666"
                fontSize="10"
              >
                {data.years[idx]}
              </text>
            </g>
          );
        })}
      </svg>

      <p className="text-[10px] text-gray-600 mt-4">{source}</p>
    </div>
  );
};

// Main component to render infographic by type
const AnimatedInfographic = ({ infographic }) => {
  if (!infographic || !infographic.data) return null;

  const { type, data, title, subtitle, source } = infographic;

  switch (type) {
    case 'table':
    case 'comparison':
      return (
        <ComparisonBarChart 
          data={data} 
          title={title} 
          subtitle={subtitle} 
          source={source} 
        />
      );
    case 'waterfall':
      return (
        <WaterfallChart 
          data={data} 
          title={title} 
          subtitle={subtitle} 
          source={source} 
        />
      );
    case 'stacked_bar':
      return (
        <StackedBarChart 
          data={data} 
          title={title} 
          subtitle={subtitle} 
          source={source} 
        />
      );
    case 'line':
      return (
        <LineChart 
          data={data} 
          title={title} 
          subtitle={subtitle} 
          source={source} 
        />
      );
    default:
      // Generic display for unknown types
      return (
        <div className="bg-[#1a1a2e] rounded-xl border border-gray-800 p-6">
          <h3 className="text-xl font-bold text-white mb-1">{title}</h3>
          <p className="text-gray-400 text-sm mb-4">{subtitle}</p>
          <div className="bg-gray-900/50 rounded p-4">
            <pre className="text-xs text-gray-400 overflow-auto">
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
          <p className="text-[10px] text-gray-600 mt-4">{source}</p>
        </div>
      );
  }
};

// Gallery component for all infographics
export const InfographicsGallery = ({ infographics = [] }) => {
  const [selectedId, setSelectedId] = useState(null);

  if (!infographics.length) {
    return (
      <div className="text-center py-12 text-gray-500">
        <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>Aucune infographie disponible</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {infographics.map((infographic) => (
          <div 
            key={infographic.id}
            className={`cursor-pointer transition-transform hover:scale-[1.02] ${
              selectedId === infographic.id ? 'ring-2 ring-blue-500 rounded-xl' : ''
            }`}
            onClick={() => setSelectedId(
              selectedId === infographic.id ? null : infographic.id
            )}
          >
            <AnimatedInfographic infographic={infographic} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default AnimatedInfographic;
